/**
 * Idempotency helpers for user-initiated write requests.
 *
 * A single user action on a property (clicking "Contact On WhatsApp", clicking
 * "Place Direct Call", opening a property details page) fans out into up to two
 * API writes: a property insight counter and a property lead row.
 *
 * `POST /property_leads` dedupes on the `Idempotency-Key` header (same key =>
 * same lead row), so the rule is:
 *
 *   1. mint ONE key when the user starts the action (`startUserAction`),
 *   2. send that same key with every request the action produces,
 *   3. reuse it for repeat deliveries of the SAME action (duplicate mount,
 *      double-fired click, retry) so nothing is written twice.
 *
 * A fresh key is minted once the dedupe window lapses, so a genuine later
 * contact is still recorded as a new lead.
 */

export const IDEMPOTENCY_HEADER = "Idempotency-Key";

/**
 * How long repeat deliveries of one action are treated as that same action.
 * Long enough to swallow a double-fired click / duplicate effect run, short
 * enough that an intentional second contact is recorded separately.
 */
export const ACTION_DEDUPE_WINDOW_MS = 2000;

/**
 * RFC 4122 version 4 UUID. Prefers `crypto.randomUUID`, falls back to
 * `crypto.getRandomValues` (older Safari) and finally `Math.random` so it never
 * throws in environments without WebCrypto.
 */
export const createIdempotencyKey = (): string => {
  const cryptoObj =
    typeof globalThis !== "undefined" ? globalThis.crypto : undefined;

  if (typeof cryptoObj?.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoObj?.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
};

/** Canonical action id, e.g. `contact:whatsapp:<propertyId>`. */
export const buildActionId = (
  ...parts: (string | null | undefined)[]
): string => parts.filter(Boolean).join(":");

interface ActionKeyEntry {
  key: string;
  /** Timestamp after which the action is considered finished. */
  expiresAt: number;
}

/** Memoised key per in-flight action. Entries expire after the dedupe window. */
const actionKeys = new Map<string, ActionKeyEntry>();

const pruneExpired = (now: number): void => {
  actionKeys.forEach((entry, action) => {
    if (entry.expiresAt <= now) actionKeys.delete(action);
  });
};

/**
 * Returns the idempotency key for `action`, minting it on first use and
 * reusing it for `windowMs` so every repeat delivery of that action carries
 * the same key.
 *
 * Use this when a request must still be sent (the server dedupes it); use
 * `startUserAction` when a duplicate delivery should be skipped entirely.
 */
export const getActionIdempotencyKey = (
  action: string,
  windowMs: number = ACTION_DEDUPE_WINDOW_MS,
): string => {
  const now = Date.now();
  pruneExpired(now);

  const existing = actionKeys.get(action);
  if (existing) return existing.key;

  const key = createIdempotencyKey();
  actionKeys.set(action, { key, expiresAt: now + windowMs });
  return key;
};

/**
 * Marks the START of a user action and returns the key to send with its
 * requests.
 *
 * Returns `null` when the same action was already started inside the dedupe
 * window — the caller should then skip the work entirely, so a double-fired
 * click cannot open WhatsApp / the dialer twice or double-count the action.
 */
export const startUserAction = (
  action: string,
  windowMs: number = ACTION_DEDUPE_WINDOW_MS,
): string | null => {
  const now = Date.now();
  pruneExpired(now);

  if (actionKeys.has(action)) return null;

  const key = createIdempotencyKey();
  actionKeys.set(action, { key, expiresAt: now + windowMs });
  return key;
};

/** Forgets the memoised key for one action, or for every action. */
export const clearActionIdempotencyKey = (action?: string): void => {
  if (action) {
    actionKeys.delete(action);
  } else {
    actionKeys.clear();
  }
};

/** Ready-to-spread axios headers for an idempotent request. */
export const idempotencyHeaders = (key: string): Record<string, string> => ({
  [IDEMPOTENCY_HEADER]: key,
});
